<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import AverageSaved from '$lib/PageSections/Charts/AverageSaved/AverageSaved.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import windowReplacement from './images/energy-efficient-window-replacement.jpg';
	import manInstallingWindowInsertIntoLargeWindow from './images/manInstallingWindowInsertIntoLargeWindow.jpg';
	import windowsWithIndowInsertsInstalled from './images/windowsWithIndowInsertsInstalled.jpg';
</script>

<Section>
	<p>
		Saving energy. Easier to maintain. Lasting a lifetime. These are all the
		things replacement energy efficient windows are supposed to do for you. If
		you’re asking yourself if new windows are a good investment for your home,
		we’re here to tell you sure. But the question you should be asking yourself
		is if they’re the best investment. Replacement windows aren’t always the
		best for performance, and they certainly aren’t the cheapest option. There
		are a lot of window replacement myths out there about energy efficiency and
		benefits. We’re clearing some of them up here.
	</p>
	<Image src={windowReplacement} />
</Section>

<Section>
	<h2>Window Energy Efficiency Myths</h2>
	<nav>
		<ol class="list-decimal">
			<li>
				<a href="#energy"
					>Replacement windows are the most energy efficient windows</a
				>
			</li>
			<li>
				<a href="#maintain">New windows are easier to maintain</a>
			</li>
			<li>
				<a href="#lifetime"
					>Replacement windows last a lifetime (or even 40 years)</a
				>
			</li>
			<li>
				<a href="#value">New windows add value to a home</a>
			</li>
			<li>
				<a href="#tech">Only new windows can improve energy efficiency</a>
			</li>
		</ol>
	</nav>
</Section>

<Section id="energy">
	<h2>1. Replacement windows help you save the most energy</h2>
	<p>
		According to ENERGY STAR, you can save an average of <a
			href="https://www.energystar.gov/products/building_products/residential_windows_doors_and_skylights/benefits"
			target="_blank"
			rel="external">12% a year</a
		> on your energy bill by installing Energy Star windows. This changes depending
		on the windows you’re replacing and what kind of climate you live in.
	</p>
	<p>
		According to the <a
			href="https://www.energy.gov/energysaver/storm-windows"
			target="_blank"
			rel="external">Department of Energy</a
		>: “Storm windows will produce similar savings at a far lower initial cost.
		Some types of storm windows are also a good option for those living in
		apartments. Storm windows can help reduce air movement into and out of
		existing windows, helping to improve comfort and reduce heating and cooling
		costs.”
	</p>
	<p>
		Indow window inserts, for example, save an average of 20% on energy bills
		annually. This combined with their lowered cost means a dramatically <a
			href="/how-to/get-indow-inserts">shorter ROI</a
		>. That makes window inserts the most energy efficient window treatments.
	</p>
	<AverageSaved title={null} />
</Section>

<Section id="maintain">
	<h2>2. New windows are easier to maintain</h2>
	<p>
		Most new replacement windows boast an easy maintenance system. A sash pops
		out for easy cleaning. One of the benefits of replacement windows is no need
		for touch up paint on vinyl frames. Newer windows though, are complex <a
			href="https://www.windowparts.com/andersen-400-series-double-hung-other-parts-s/90.htm"
			target="_blank"
			rel="external">with many moving parts</a
		>, if any one of those parts breaks, the entire assembly could be
		compromised. Even if you can get a replacement part, the damage done in the
		meantime by nature could make it too late. How long do vinyl windows last?
		Only until the seal is broken.
	</p>
	<p>
		Window inserts have no moving parts. If the seal is broken, you simply
		remove the insert and reinstall it.
	</p>
	<Image
		alt="Exterior of home with Indow inserts: How does the Indow warranty compare to replacement warranty?"
		src={windowsWithIndowInsertsInstalled}
		class="w-full"
	/>
</Section>

<Section id="lifetime">
	<h2>3. Replacement windows last a lifetime</h2>
	<p>
		Lifetime warranty. That’s one of the huge replacement window benefits,
		right? But how long are window warranties? If you look closely at this
		warranty, it’s generally <a
			href="https://thecraftsmanblog.com/replacement-windows-the-real-story"
			target="_blank"
			rel="external">20-25 years</a
		>. And that’s usually for just the glass. Other parts of the assembly of the
		window could be warrantied for less. Lower quality replacement windows can
		come with warranties less than 10 years.
	</p>
	<p>
		So, how long should vinyl windows last? Professionals agree 15 – 20 years
		for new, quality windows. That’s hardly a lifetime. It’s no surprise then
		that their warranties last exactly this long. A window’s warranty typically
		will not exceed its life expectancy. It used to be that homes, including
		windows, were passed down through generations. Now, it’s acceptable to call
		20 years a lifetime. Since windows are not covered by home warranties, you
		want to make sure your window solution has a warranty that will give you
		peace of mind. Indow inserts come with a warranty that lasts as long as the
		purchaser remains in the home.
	</p>
</Section>

<Section id="value">
	<h2>4. Replacement windows add resale value</h2>
	<p>
		Many new homebuyers are looking for green homes, those that will keep
		utility costs down and create a smaller carbon footprint. If your current
		windows are not energy efficient, it might be worthwhile to address the
		issue. But new windows won’t necessarily add value to your house. Window
		replacement projects often cost a couple thousand dollars more than the
		value that new windows add to your home. If you have a historic home,
		replacing the original windows will likely hurt the value.
	</p>
	<p>
		Retrofitting windows with inserts will cut utility costs and shrink your
		carbon footprint while prolonging the life of the original windows and
		maintaining the curb appeal of your home. New windows will look out of place
		with the rest of the home design.
	</p>
	<Image
		alt="Man installing insert: Replacements better than window retrofits? It’s window replacement myth"
		src={manInstallingWindowInsertIntoLargeWindow}
	/>
</Section>

<Section id="tech">
	<h2>
		5. Only New Technology of Replacement Windows Can Provide Better Energy
		Efficiency
	</h2>
	<p>
		Window inserts are the best energy efficient window treatments, despite the
		new technology available with replacement windows.
	</p>
	<p>
		New replacement windows can seal gas between the two panes that further help
		with thermal insulation. The gas, usually argon, helps with heat gain and
		heat loss because it is less convective than air. However, the inserted gas
		does <a
			href="https://www.nachi.org/window-gas-fills.htm"
			target="_blank"
			rel="external">leak over time</a
		>. Leaks can cause the windows to appear foggy. In most cases, the leak
		follows the same timeline as the window, remaining effective for 20 years.
		In some cases, the seal will break, releasing all the gas at once. With a
		window insert, you would just remove and reinstall to recreate the seal.
		Argon gas is non-toxic, nonflammable, and non-reactive. Another
		misconception about argon-filled windows is that the gas between the panes
		will help with noise reduction. This is not true. Windows with argon gas
		will help with energy efficiency, but they are not an appropriate solution
		for noise reduction.
	</p>
	<p>
		Other replacement window benefits include Low-E coatings. Low-E coatings
		come in a variety of choices. They all can lower the solar heat-gain
		coefficient (a lower SHGC means less heat passes through the window). The
		benefits of replacement windows with Low-E coating are hard to argue with,
		but window inserts can provide the same thing. Indow Shade Grade has a SHGC
		of .49 (reducing solar heat-gain by 51%), 57.5% visible light transmittance,
		R-value listing of 1.96 and UV reduction of 90%.
	</p>
</Section>

<Section>
	<h2>Still Mystified?</h2>
	<p>
		There are so many myths about windows out there, we don’t blame you. The
		good news is, we’ve done a lot of research and can talk you through it. You
		probably don’t have to replace all your windows, maybe only some and add
		storm windows or inserts to others.
	</p>
	<p>
		Whatever window issue you are facing, we’d love to help! <a
			href="/about-indow/contact">Give us a call</a
		> and we can talk about what the best option is for you.
	</p>
</Section>

<Reviews />
